import React, { useState } from "react";
import KuikaButton from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaPopconfirm, { KuikaPopconfirmTone } from "../../../../ui-kit/kuika-popconfirm/kuika-popconfirm";
import KuikaSmallText from "../../../../ui-kit/typography/SmallText/SmallText";
import { ShowcaseFrame } from "../_layout/showcase-frame";
import { SpecRow } from "../_layout/spec-row";
import { VariantGrid } from "../_layout/variant-grid";

type PopconfirmPosition = "top" | "right" | "bottom" | "left";

const POSITIONS: PopconfirmPosition[] = ["top", "right", "bottom", "left"];
const TONES: KuikaPopconfirmTone[] = ["default", "danger"];

interface IConfirmDemoProps {
  position?: PopconfirmPosition;
  tone?: KuikaPopconfirmTone;
  title?: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  triggerLabel: string;
}

const ConfirmDemo: React.FC<IConfirmDemoProps> = ({
  position = "top",
  tone = "default",
  title = "Remove this role?",
  description,
  confirmLabel,
  cancelLabel,
  triggerLabel
}: IConfirmDemoProps): JSX.Element => (
  <KuikaPopconfirm
    position={position}
    tone={tone}
    title={title}
    description={description}
    confirmLabel={confirmLabel}
    cancelLabel={cancelLabel}
    onConfirm={(): void => undefined}
  >
    <KuikaButton type="secondary" size="sm">
      {triggerLabel}
    </KuikaButton>
  </KuikaPopconfirm>
);

/** The shape a panel uses when the question is raised by something OTHER than the thing it points
 *  at — a row's "…" menu, say. The popconfirm anchors to the row and the panel owns `open`, which
 *  is how the authorization panel asks before it edits or deletes a role. */
const ControlledDemo: React.FC = (): JSX.Element => {
  const [open, setOpen] = useState<boolean>(false);
  const [answer, setAnswer] = useState<string>("no answer yet");

  return (
    <div className="kuika-flex-center--gap-m">
      <KuikaPopconfirm
        open={open}
        onOpenChange={setOpen}
        tone="danger"
        title="Delete “Administrator”?"
        description="Screens bound to it lose their authorization."
        confirmLabel="Yes, Delete"
        cancelLabel="No, Cancel"
        onConfirm={(): void => setAnswer("deleted")}
        onCancel={(): void => setAnswer("kept")}
      >
        <KuikaButton type="secondary" size="sm">
          Administrator
        </KuikaButton>
      </KuikaPopconfirm>
      <KuikaButton type="text" color="danger" size="sm" onClick={(): void => setOpen(true)}>
        Delete from the row menu
      </KuikaButton>
      <KuikaSmallText>{answer}</KuikaSmallText>
    </div>
  );
};

export const PopconfirmStory: React.FC = (): JSX.Element => {
  return (
    <ShowcaseFrame
      title="Popconfirm"
      componentName="KuikaPopconfirm"
      sourcePath="ui-kit/kuika-popconfirm"
      description="The yes/no asked where the action is, on a KuikaPopover surface with the alertdialog role. Four positions, a danger tone for the irreversible answer, controlled or on its own."
    >
      <VariantGrid
        label="position"
        cols={POSITIONS}
        cellMinWidth={160}
        render={(position: string): React.ReactNode => (
          <ConfirmDemo position={position as PopconfirmPosition} triggerLabel={position} />
        )}
      />
      <VariantGrid
        label="tone"
        cols={TONES}
        cellMinWidth={160}
        render={(tone: string): React.ReactNode => (
          <ConfirmDemo
            tone={tone as KuikaPopconfirmTone}
            title={tone === "danger" ? "Delete this role?" : "Apply this change?"}
            triggerLabel={tone}
          />
        )}
      />
      <SpecRow label="With a description" caption="the consequence, not a restatement of the question">
        <ConfirmDemo
          tone="danger"
          title="Delete “Administrator”?"
          description="Screens bound to it lose their authorization."
          triggerLabel="Delete role"
        />
      </SpecRow>
      <SpecRow label="Custom labels" caption='confirmLabel / cancelLabel — default "Yes" / "No"'>
        <ConfirmDemo
          title="Rename this role everywhere?"
          confirmLabel="Yes, Proceed"
          cancelLabel="No, Cancel"
          triggerLabel="Rename role"
        />
      </SpecRow>
      <SpecRow
        label="Controlled"
        caption="open + onOpenChange — raised from elsewhere, anchored to the row; Escape or a press outside answers cancel"
      >
        <ControlledDemo />
      </SpecRow>
    </ShowcaseFrame>
  );
};
